import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";
import { ArrowLeftIcon, PaperAirplaneIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
    createSupportTicket,
    listSupportTickets,
    getSupportTicket,
    replyToSupportTicket,
} from "@/api/supportApi";
import type { TicketDetail, TicketSummary } from "@/types/SupportTicket";

const SANITIZE_CONFIG = {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "a", "ul", "ol", "li", "blockquote", "code", "pre", "span", "h1", "h2", "h3", "h4"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
};

const MessageBody: React.FC<{ html: string }> = ({ html }) => {
    const sanitized = useMemo(() => DOMPurify.sanitize(html, SANITIZE_CONFIG), [html]);
    return (
        <div
            className="text-sm text-gray-800 helpio-message-body"
            dangerouslySetInnerHTML={{ __html: sanitized }}
        />
    );
};

type View = "list" | "new" | "detail";

interface HelpContactFormProps {
    initialTicketId?: string | null;
    onTicketSelected?: (ticketId: string | null) => void;
}

const statusColors: Record<string, string> = {
    open: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    resolved: "bg-blue-100 text-blue-800",
    closed: "bg-gray-100 text-gray-800",
};

const formatDateTime = (iso?: string) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const HelpContactForm: React.FC<HelpContactFormProps> = ({ initialTicketId = null, onTicketSelected }) => {
    const { t } = useTranslation();
    const location = useLocation();

    const [view, setView] = useState<View>(initialTicketId ? "detail" : "list");
    const [filter, setFilter] = useState<"ongoing" | "closed">("ongoing");

    const [tickets, setTickets] = useState<TicketSummary[]>([]);
    const [loadingTickets, setLoadingTickets] = useState(false);

    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(initialTicketId);
    const [ticketDetail, setTicketDetail] = useState<TicketDetail | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [replyBody, setReplyBody] = useState("");
    const [replying, setReplying] = useState(false);

    useEffect(() => {
        if (initialTicketId) {
            setSelectedTicketId(initialTicketId);
            setView("detail");
        }
    }, [initialTicketId]);

    useEffect(() => {
        if (view !== "list") return;
        let cancelled = false;
        const load = async () => {
            setLoadingTickets(true);
            try {
                const data = await listSupportTickets(filter);
                if (!cancelled) setTickets(data);
            } catch {
                if (!cancelled) toast.error(t("support.tickets.loadError"));
            } finally {
                if (!cancelled) setLoadingTickets(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [view, filter, t]);

    useEffect(() => {
        if (view !== "detail" || !selectedTicketId) return;
        let cancelled = false;
        const load = async () => {
            setLoadingDetail(true);
            setTicketDetail(null);
            try {
                const data = await getSupportTicket(selectedTicketId);
                if (!cancelled) setTicketDetail(data);
            } catch {
                if (!cancelled) toast.error(t("support.tickets.detailError"));
            } finally {
                if (!cancelled) setLoadingDetail(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [view, selectedTicketId, t]);

    const goToList = () => {
        setView("list");
        setSelectedTicketId(null);
        setTicketDetail(null);
        setReplyBody("");
        onTicketSelected?.(null);
    };

    const openTicket = (id: string) => {
        setSelectedTicketId(id);
        setView("detail");
        onTicketSelected?.(id);
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim() || submitting) return;
        setSubmitting(true);
        try {
            await createSupportTicket({
                subject: subject.trim() || undefined,
                message: message.trim(),
                route: location.pathname,
            });
            toast.success(t("support.tickets.createSuccess"));
            setSubject("");
            setMessage("");
            goToList();
        } catch {
            toast.error(t("support.tickets.createError"));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = async () => {
        if (!replyBody.trim() || !selectedTicketId || replying) return;
        setReplying(true);
        try {
            await replyToSupportTicket(selectedTicketId, replyBody.trim());
            setReplyBody("");
            const data = await getSupportTicket(selectedTicketId);
            setTicketDetail(data);
        } catch {
            toast.error(t("support.tickets.replyError"));
        } finally {
            setReplying(false);
        }
    };

    if (view === "list") {
        return (
            <div className="flex flex-col h-full">
                <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                        {(["ongoing", "closed"] as const).map((f) => (
                            <button
                                key={f}
                                type="button"
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                                    filter === f ? "bg-white shadow-sm text-gray-900" : "text-gray-600"
                                }`}
                            >
                                {t(`support.tickets.filter${f === "ongoing" ? "Ongoing" : "Closed"}`)}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setView("new")}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition"
                    >
                        <PlusIcon className="w-4 h-4" />
                        {t("support.tickets.new")}
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 pb-6">
                    {loadingTickets && (
                        <p className="text-sm text-gray-500 text-center mt-8">{t("common.loading", "Laddar…")}</p>
                    )}
                    {!loadingTickets && tickets.length === 0 && (
                        <div className="text-center mt-10">
                            <p className="text-sm text-gray-500 mb-3">{t("support.tickets.empty")}</p>
                            <button
                                type="button"
                                onClick={() => setView("new")}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                {t("support.tickets.createFirst")}
                            </button>
                        </div>
                    )}
                    <ul className="space-y-2">
                        {tickets.map((ticket) => (
                            <li key={ticket.id}>
                                <button
                                    type="button"
                                    onClick={() => openTicket(ticket.id)}
                                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <span className="text-sm font-medium text-gray-900 truncate">
                                            #{ticket.number} · {ticket.subject || t("support.tickets.unknown")}
                                        </span>
                                        <span
                                            className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                                statusColors[ticket.status] ?? "bg-gray-100 text-gray-800"
                                            }`}
                                        >
                                            {ticket.status_name || ticket.status}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{formatDateTime(ticket.created_at)}</p>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    if (view === "new") {
        return (
            <div className="flex flex-col h-full">
                <div className="px-5 pt-4 pb-2">
                    <button
                        type="button"
                        onClick={goToList}
                        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        {t("common.back", "Tillbaka")}
                    </button>
                </div>
                <form onSubmit={handleCreate} className="flex-1 overflow-y-auto px-5 pb-4 flex flex-col gap-4">
                    <div>
                        <label htmlFor="help-contact-subject" className="block text-sm font-medium text-gray-800 mb-1">
                            {t("help.contact.subject.label")}
                        </label>
                        <input
                            id="help-contact-subject"
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder={t("help.contact.subject.placeholder")}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="help-contact-message" className="block text-sm font-medium text-gray-800 mb-1">
                            {t("help.contact.message.label")}
                        </label>
                        <textarea
                            id="help-contact-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder={t("help.contact.message.placeholder")}
                            rows={6}
                            required
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={!message.trim() || submitting}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        {submitting ? t("help.contact.sending") : t("help.contact.send")}
                    </button>
                </form>
            </div>
        );
    }

    // detail
    return (
        <div className="flex flex-col h-full">
            <div className="px-5 pt-4 pb-2">
                <button
                    type="button"
                    onClick={goToList}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    {t("common.back", "Tillbaka")}
                </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4">
                {loadingDetail && (
                    <p className="text-sm text-gray-500 text-center mt-8">{t("common.loading", "Laddar…")}</p>
                )}
                {!loadingDetail && !ticketDetail && (
                    <p className="text-sm text-gray-500 text-center mt-8">{t("support.tickets.notFound")}</p>
                )}
                {ticketDetail && (
                    <div className="space-y-4">
                        <div>
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <h3 className="text-base font-semibold text-gray-900">
                                    #{ticketDetail.number} · {ticketDetail.subject}
                                </h3>
                                <span
                                    className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                        statusColors[ticketDetail.status] ?? "bg-gray-100 text-gray-800"
                                    }`}
                                >
                                    {ticketDetail.status_name || ticketDetail.status}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">{formatDateTime(ticketDetail.created_at)}</p>
                        </div>
                        {ticketDetail.body && (
                            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                <p className="text-xs text-gray-500 mb-1">{t("support.tickets.originalMessage")}</p>
                                <p className="text-sm text-gray-800 whitespace-pre-line">{ticketDetail.body}</p>
                            </div>
                        )}
                        <div className="space-y-3">
                            {ticketDetail.messages.map((m) => {
                                const fromAgent = m.author_type === "agent";
                                const fromSystem = m.author_type === "system";
                                return (
                                    <div
                                        key={m.id}
                                        className={`p-3 rounded-lg ${
                                            fromSystem
                                                ? "bg-gray-50 border border-gray-200"
                                                : fromAgent
                                                ? "bg-blue-50 border border-blue-100"
                                                : "bg-white border border-gray-200"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-gray-700">
                                                {fromAgent
                                                    ? m.author_name || t("support.tickets.agent")
                                                    : fromSystem
                                                    ? t("support.tickets.system")
                                                    : t("support.tickets.you")}
                                            </span>
                                            <span className="text-xs text-gray-500">{formatDateTime(m.created_at)}</span>
                                        </div>
                                        <MessageBody html={m.body} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            {ticketDetail && ticketDetail.status !== "closed" && (
                <div className="border-t border-gray-200 px-5 py-3">
                    <div className="flex gap-2 items-end">
                        <textarea
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            placeholder={t("support.tickets.replyPlaceholder")}
                            rows={2}
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                        />
                        <button
                            type="button"
                            onClick={handleReply}
                            disabled={!replyBody.trim() || replying}
                            className="flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex-shrink-0"
                            aria-label={t("support.tickets.reply")}
                        >
                            <PaperAirplaneIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HelpContactForm;
